// Helpers to parse YouTube/Dailymotion URLs and produce a minimal `video` object

export const parseYoutubeId = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.has("v")) return u.searchParams.get("v");
    const parts = u.pathname.split("/");
    const embedIdx = parts.indexOf("embed");
    if (embedIdx !== -1 && parts.length > embedIdx + 1) return parts[embedIdx + 1];
    return null;
  } catch (e) {
    return null;
  }
};

export const parseDailymotionId = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    // Dailymotion formats: https://www.dailymotion.com/video/{id}
    const parts = u.pathname.split("/").filter(Boolean);
    const idx = parts.indexOf("video");
    if (idx !== -1 && parts.length > idx + 1) return parts[idx + 1];
    return null;
  } catch (e) {
    return null;
  }
};

export const getThumbnailUrl = (url) => {
  const yt = parseYoutubeId(url);
  if (yt) return `https://i.ytimg.com/vi/${yt}/hqdefault.jpg`;
  const dm = parseDailymotionId(url);
  if (dm) return `https://www.dailymotion.com/thumbnail/video/${dm}`;
  return "";
};

const deriveTitleFromUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    const yt = parseYoutubeId(url);
    if (yt) return `YouTube video ${yt}`;
    const dm = parseDailymotionId(url);
    if (dm) return `Dailymotion video ${dm}`;
    // Try to derive readable text from last path segment
    const parts = u.pathname.split('/').filter(Boolean);
    // For Facebook, try to pick a meaningful segment (often the title appears before numeric id)
    if (u.hostname.includes('facebook.com') || u.hostname.includes('fb.watch')) {
      // Find the last non-numeric segment with length > 2
      for (let i = parts.length - 1; i >= 0; i--) {
        const seg = parts[i];
        if (!seg) continue;
        // skip pure numeric ids
        if (/^\d+$/.test(seg)) continue;
        // decode and clean
        try {
          const decoded = decodeURIComponent(seg).replace(/[-_]+/g, ' ').replace(/\.(html|htm|php|aspx)$/i, '');
          if (decoded && decoded.length > 2) return decoded.replace(/^./, (c) => c.toUpperCase());
        } catch (e) {
          continue;
        }
      }
      return 'Facebook video';
    }
    if (parts.length > 0) {
      const last = parts[parts.length - 1];
      // decode and replace separators
      const decoded = decodeURIComponent(last.replace(/[-_]+/g, ' ')).replace(/\.(html|htm|php|aspx)$/i, '');
      if (decoded && decoded.length > 0) {
        // Capitalize first letter
        return decoded.replace(/^./, (c) => c.toUpperCase());
      }
    }
    // fallback to hostname
    return u.hostname;
  } catch (e) {
    return null;
  }
};

// Attempt to capture a frame from a video URL and return a data URL (best-effort).
// Returns `null` if capture is not possible (CORS, non-video URL, timeout, etc.).
export const captureVideoFrame = (videoUrl, seekTo = 1.0, timeout = 8000) => {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || !videoUrl) return resolve(null);

    try {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.src = videoUrl;

      let settled = false;
      const cleanup = () => {
        try {
          video.pause();
          video.removeAttribute('src');
          video.load && video.load();
          video.remove();
        } catch (e) {}
      };

      const fail = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(null);
      };

      const capture = () => {
        if (settled) return;
        try {
          const w = video.videoWidth || 320;
          const h = video.videoHeight || 180;
          if (!w || !h) return fail();
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          settled = true;
          cleanup();
          resolve(dataUrl);
        } catch (e) {
          fail();
        }
      };

      const onLoaded = () => {
        try {
          // Try to seek to the requested time to get a representative frame
          if (video.duration && seekTo < video.duration) {
            const onSeek = () => {
              capture();
              video.removeEventListener('seeked', onSeek);
            };
            video.addEventListener('seeked', onSeek);
            try { video.currentTime = Math.min(seekTo, video.duration); } catch (e) { capture(); }
          } else {
            capture();
          }
        } catch (e) { fail(); }
      };

      const onError = () => fail();

      const timer = setTimeout(() => { clearTimeout(timer); fail(); }, timeout);

      video.addEventListener('loadeddata', onLoaded, { once: true });
      video.addEventListener('error', onError, { once: true });

      // Some browsers require play() for certain media to progress. Attempt play (muted).
      // Ignore play promise failures.
      video.play && video.play().catch(() => {});
    } catch (e) {
      resolve(null);
    }
  });
};

// Convert a minimal content entry into the app's `video` object shape
export const toAppVideo = (item) => {
  let url = item?.url || item?.originalUrl;
  // If the URL was percent-encoded (e.g., coming from external scraper), decode it for client use
  try {
    if (typeof url === 'string' && !/^https?:\/\//i.test(url) && url.indexOf('%3A') !== -1) {
      const dec = decodeURIComponent(url);
      if (/^https?:\/\//i.test(dec)) url = dec;
    }
  } catch (e) {
    // ignore decode errors
  }
  const yt = parseYoutubeId(url);
  const dm = parseDailymotionId(url);
  // detect facebook URLs explicitly - check platform/source first, then URL
  const isFacebook = (item?.platform === 'facebook' || item?.source === 'facebook') || 
    (typeof url === 'string' && (url.includes('facebook.com') || url.includes('fb.watch')));

  // For Facebook, prefer the videoId from the item if available
  let fbVideoId = null;
  if (isFacebook) {
    fbVideoId = item?.videoId;
    if (!fbVideoId && url) {
      // Try to extract from URL
      const reelMatch = url.match(/\/reel\/(\d+)/);
      const videosMatch = url.match(/\/videos\/[^/]*?(\d{10,})/);
      const watchMatch = url.match(/[?&]v=(\d+)/);
      fbVideoId = reelMatch?.[1] || videosMatch?.[1] || watchMatch?.[1];
    }
  }

  // derive a stable id: prefer provided videoId from the source, else use youtube/dailymotion id, else fallback to encoded url
  const id = item?.videoId || yt || dm || fbVideoId || (isFacebook ? encodeURIComponent(url) : null);
  if (!id) return null;

  // Clean and normalize title values (decode percent-encoding and HTML entities)
  const cleanTitle = (raw) => {
    if (!raw || typeof raw !== 'string') return raw;
    let s = raw.trim();
    try {
      // If the title looks like a percent-encoded URL or contains encodings, try decode
      if (/%3A|%2F|%25/.test(s) || /^https%3A/i.test(s)) {
        try { s = decodeURIComponent(s); } catch (e) { /* ignore */ }
      }
    } catch (e) {}

    // Decode common HTML entities when running in browser
    try {
      if (typeof document !== 'undefined') {
        const ta = document.createElement('textarea');
        ta.innerHTML = s;
        s = ta.value;
      } else {
        s = s.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
      }
    } catch (e) {}

    return s;
  };

  // Get title - prefer item.title, but check if it's a valid title (not just a number or URL)
  let rawTitle = item?.title;
  const isValidTitle = (t) => {
    if (!t || typeof t !== 'string') return false;
    const trimmed = t.trim();
    if (trimmed.length < 3) return false;
    if (/^\d+$/.test(trimmed)) return false; // Just numbers (video ID)
    if (/^https?:\/\//i.test(trimmed)) return false; // URL
    if (/^https%3A/i.test(trimmed)) return false; // Encoded URL
    return true;
  };
  
  if (!isValidTitle(rawTitle)) {
    rawTitle = deriveTitleFromUrl(url) || "(No title)";
  }
  const finalTitle = cleanTitle(rawTitle);

  // Build embed URL for Facebook if not provided - use original URL for href
  let embedUrl = item?.embed || item?.pluginUrl || item?.src;
  if (isFacebook && !embedUrl && url) {
    embedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&t=0`;
  }

  // Handle thumbnails - prefer from item, then from thumbnails array
  let thumbnailUrl = item?.thumbnail || (item?.thumbnails && item.thumbnails[0]?.url) || getThumbnailUrl(url);

  const result = {
    videoId: id,
    // keep the original url so consumers can decide how to render (ReactPlayer or custom iframe)
    originalUrl: url || null,
    url: url || null,
    platform: isFacebook ? 'facebook' : (yt ? 'youtube' : (dm ? 'dailymotion' : null)),
    source: isFacebook ? 'facebook' : (yt ? 'youtube' : (dm ? 'dailymotion' : null)),
    title: finalTitle,
    // Prefer thumbnails provided by the DB payload (e.g. FB oEmbed / scraped og:image).
    // Fallback to computed thumbnails for known platforms (YouTube / Dailymotion).
    thumbnails: thumbnailUrl ? [{ url: thumbnailUrl }] : [{ url: getThumbnailUrl(url) }],
    thumbnail: thumbnailUrl,
    author: { title: item?.author || "Uploaded", avatar: [{ url: "https://www.gravatar.com/avatar/?d=mp" }] },
    // support multiple shapes: item.stats.views, item.views, item.video?.stats, item.video?.views
    stats: {
      views:
        (item && (item.stats?.views ?? item.views)) ?? (item?.video && (item.video.stats?.views ?? item.video.views)) ?? 0,
      likes:
        (item && (item.stats?.likes ?? item.likes)) ?? (item?.video && (item.video.stats?.likes ?? item.video.likes)) ?? 0,
    },
    publishedTimeText: item?.published || "",
    lengthSeconds: item?.lengthSeconds || null,
    customTag: item?.tag || item?.category || null,
    width: item?.width || null,
    height: item?.height || null,
    // Facebook-specific fields
    embed: embedUrl,
    pluginUrl: embedUrl,
    src: embedUrl,
  };

  console.log('[toAppVideo] converted:', { 
    input: { platform: item?.platform, source: item?.source, url: item?.url, title: item?.title },
    output: { platform: result.platform, source: result.source, originalUrl: result.originalUrl, embed: result.embed, title: result.title }
  });

  return result;
};

export { deriveTitleFromUrl };
